import type { Merkposten } from "./merkliste";

/**
 * Der **gerechnete** Satz je Einheit — die Zahl, gegen die ein Preis von Hand
 * und der Satz von damals stehen.
 *
 * Sie kommt aus dem Posten und nicht aus einer Abfrage: `netto` war der
 * gerechnete Betrag, `einheiten` seine Stückzahl. An einer Zeile ohne Tarif
 * (Leistung, Ware, Zuschlag) steht in `netto` schon der Satz selbst.
 */
export function gerechneterSatz(p: Merkposten): number {
  return Math.round(
    (p.einheiten && p.einheiten > 0 ? p.netto / p.einheiten : p.netto) * 100,
  ) / 100;
}

/**
 * Der Satz von damals, **wenn er etwas zu sagen hat** — sonst nichts.
 *
 * Zwei Fälle, zwei Sätze (wortgleich mit der alten Schnellerfassung):
 * `nurQuelle` heißt „im Stamm steht heute keiner, die Zahl ist die von
 * damals" — eine Herkunftsangabe, kein Vergleich. Sonst ist es ein
 * Vergleich, und der steht nur da, wenn die beiden Zahlen sich
 * unterscheiden: *„Preis unverändert"* stünde bei jeder zweiten
 * Wiederholung und würde nicht gelesen.
 *
 * **Verglichen wird gegen den Satz, der gilt** — von Hand vor gerechnet. Wer
 * den alten Preis von Hand wieder hinschreibt, hat die Abweichung
 * ausgeräumt, und die Marke oben soll das merken.
 */
export function damalsWert(
  p: Merkposten,
): { betrag: number; nurQuelle: boolean; einheitenDamals?: number } | null {
  const damals = p.vorlage?.satz;
  if (damals === undefined || damals === null) return null;
  if (p.vorlage?.ohneStamm) return { betrag: damals, nurQuelle: true };
  /* Solange der Preis für den neuen Zeitraum nicht da ist, gibt es nichts zu
     vergleichen. Eine Abweichung gegen die Null wäre eine erfundene. */
  if (!p.gerechnet) return null;
  const heute = p.einzelpreis ?? gerechneterSatz(p);
  if (Math.round(heute * 100) === Math.round(damals * 100)) return null;
  /* **Eine andere Dauer ist kein anderer Preis** (Prüfrunde Runde 10, „Neu
     gesehen"): damals 1 Miettag × 35,00, jetzt 7 × 22,00 — die Staffel, nicht
     der Stamm. Dann steht der Satz von damals mit seiner Dauer da, und die
     Marke oben zählt ihn nicht als geänderten Preis. */
  const v = p.vorlage;
  if (v?.einheiten && p.einheiten && v.einheiten !== p.einheiten
    && (!v.einheitKey || !p.einheitKey || v.einheitKey === p.einheitKey)) {
    return { betrag: damals, nurQuelle: false, einheitenDamals: v.einheiten };
  }
  return { betrag: damals, nurQuelle: false };
}

/**
 * Was die Sammlung an Wiederholtem trägt — die beiden Marken über den Karten.
 *
 * Gezählt wird über die **ganze** Sammlung und nicht je Karte: „Zwei Preise
 * sind heute andere" ist der Satz, den jemand am Telefon braucht, bevor er
 * die Liste durchgeht. Die Nummern stehen daneben, weil eine Sammlung aus
 * zwei alten Vorgängen entstehen darf.
 */
export function vorlagenstand(posten: Merkposten[]): {
  nummern: string[]; datum: string; zeilen: number; anders: number; ohneStamm: number;
} {
  const mitVorlage = posten.filter((p) => p.vorlage);
  const werte = mitVorlage.map(damalsWert);
  return {
    nummern: [...new Set(mitVorlage.map((p) => p.vorlage?.nr ?? "").filter(Boolean))],
    datum: mitVorlage[0]?.vorlage?.datum ?? "",
    zeilen: mitVorlage.length,
    // Nur, was bei gleicher Dauer anders ist — eine andere Staffelstufe ist kein neuer Preis.
    anders: werte.filter((w) => w && !w.nurQuelle && w.einheitenDamals === undefined).length,
    ohneStamm: werte.filter((w) => w?.nurQuelle).length,
  };
}

